import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    LineChart, Loader2, RefreshCw, TrendingUp, Layers, Grid,
    Percent, Users, MapPin, Briefcase, ShieldCheck,
} from "lucide-react";
import api from "../../../lib/api";
import { useAuth } from "../../../lib/auth";
import { ROLES } from "../../../lib/roles";
import { Button } from "../../../components/ui/button";

function pct(n) {
    if (n === null || n === undefined) return "—";
    return `${Number(n).toFixed(2)}%`;
}
function money(n, cur = "USD") {
    if (n === null || n === undefined) return "—";
    return Number(n).toLocaleString("es-MX", {
        style: "currency", currency: cur, maximumFractionDigits: 0,
    });
}

// Heatmap por bucket DPD para vintages
function VintageHeatmap({ vintages }) {
    const months = Object.keys(vintages).sort();
    if (!months.length) return (
        <div className="text-sm text-muted-foreground py-8 text-center">
            Sin vintages disponibles.
        </div>
    );
    // Colorear cell según pct: 0=verde, 1-3=amarillo suave, 3-5=amarillo, 5-10=coral tenue, >10=coral
    const cellColor = (v) => {
        const n = Number(v || 0);
        if (n === 0) return "bg-[color:var(--brand-exito)]/10 text-[color:var(--brand-exito)]";
        if (n < 3) return "bg-[color:var(--brand-gold)]/15 text-[color:var(--brand-dark-gold)]";
        if (n < 5) return "bg-[color:var(--brand-gold)]/30 text-[color:var(--brand-dark-gold)]";
        if (n < 10) return "bg-[color:var(--brand-coral)]/20 text-[color:var(--brand-coral)]";
        return "bg-[color:var(--brand-coral)]/40 text-[color:var(--brand-coral)]";
    };
    return (
        <div className="overflow-x-auto">
            <table className="trf-table" data-testid="analytics-vintage-table">
                <thead>
                    <tr>
                        <th>Vintage</th>
                        <th className="text-right">Originados</th>
                        <th className="text-right">Principal</th>
                        <th className="text-right">DPD30+</th>
                        <th className="text-right">DPD90+</th>
                        <th className="text-right">Castigado</th>
                    </tr>
                </thead>
                <tbody>
                    {months.map((m) => {
                        const v = vintages[m];
                        return (
                            <tr key={m} data-testid={`vintage-${m}`}>
                                <td className="tabular font-medium">{m}</td>
                                <td className="text-right tabular">{v.originado_count}</td>
                                <td className="text-right tabular">{money(v.principal_originado, "USD")}</td>
                                <td className={`text-right tabular font-medium ${cellColor(v.dpd30_pct).split(" ")[1]}`}>
                                    <span className={`px-2 py-0.5 rounded-sm ${cellColor(v.dpd30_pct)}`}>
                                        {pct(v.dpd30_pct)}
                                    </span>
                                </td>
                                <td className={`text-right tabular font-medium ${cellColor(v.dpd90_pct).split(" ")[1]}`}>
                                    <span className={`px-2 py-0.5 rounded-sm ${cellColor(v.dpd90_pct)}`}>
                                        {pct(v.dpd90_pct)}
                                    </span>
                                </td>
                                <td className={`text-right tabular font-medium ${cellColor(v.castigado_pct).split(" ")[1]}`}>
                                    <span className={`px-2 py-0.5 rounded-sm ${cellColor(v.castigado_pct)}`}>
                                        {pct(v.castigado_pct)}
                                    </span>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function RollRatesMatrix({ rollRates }) {
    if (!rollRates || !Object.keys(rollRates).length) {
        return (
            <div className="text-sm text-muted-foreground py-8 text-center">
                Roll-rates requieren 2 cortes sucesivos.
            </div>
        );
    }
    const rows = [
        { label: "Current → DPD30", key: "current_to_dpd30" },
        { label: "DPD30 → DPD60", key: "dpd30_to_dpd60" },
        { label: "DPD60 → DPD90", key: "dpd60_to_dpd90" },
        { label: "DPD90 → Castigado", key: "dpd90_to_castigado" },
    ];
    const color = (v) => {
        const n = Number(v || 0);
        if (n === 0) return "text-[color:var(--brand-exito)]";
        if (n < 3) return "text-[color:var(--brand-dark-gold)]";
        return "text-[color:var(--brand-coral)]";
    };
    return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="rollrates-matrix">
            {rows.map((r) => (
                <div key={r.key} className="rounded-md border border-border p-4 bg-card">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                        {r.label}
                    </div>
                    <div className={`font-display text-3xl mt-2 ${color(rollRates[r.key])}`}>
                        {pct(rollRates[r.key])}
                    </div>
                </div>
            ))}
        </div>
    );
}

function ConcentrationList({ items, icon: Icon, title, testid }) {
    return (
        <div className="rounded-md border border-border bg-card p-4">
            <div className="flex items-center gap-2 mb-3">
                <Icon className="h-4 w-4 text-[color:var(--brand-dark-gold)]" />
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                    {title}
                </div>
            </div>
            <ul className="space-y-1" data-testid={testid}>
                {items?.slice(0, 10).map((item, i) => (
                    <li key={i} className="flex items-center justify-between text-sm py-1">
                        <span className="truncate max-w-[60%]">{item.nombre}</span>
                        <span className="flex items-center gap-2 tabular text-xs">
                            <span className="text-muted-foreground">{money(item.saldo)}</span>
                            <span className="font-medium">{pct(item.pct)}</span>
                        </span>
                    </li>
                ))}
                {!items?.length && (
                    <li className="text-xs text-muted-foreground italic py-2">Sin datos</li>
                )}
            </ul>
        </div>
    );
}

export default function AnalyticsTab({ facility, readOnly = false }) {
    const { hasRole } = useAuth();
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    const canRecalc = !readOnly && hasRole([
        ROLES.ADMINISTRADOR_FIDUCIARIO, ROLES.TESORERIA, ROLES.OPERACIONES,
        ROLES.ADMIN_TI, ROLES.RIESGOS,
    ]);

    const load = async () => {
        setLoading(true);
        try {
            const url = readOnly
                ? `/portal/facilities/${facility.id}/analytics`
                : `/facilities/${facility.id}/analytics/actual`;
            const { data } = await api.get(url);
            if (data?.available === false) {
                setAnalytics(null);
            } else {
                setAnalytics(data);
            }
        } catch (err) {
            if (err?.response?.status === 404) {
                setAnalytics(null);
            } else {
                toast.error("Error cargando analytics");
            }
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [facility.id]);

    const recalc = async () => {
        setBusy(true);
        try {
            await api.post(`/facilities/${facility.id}/analytics/calcular`);
            toast.success("Analytics recalculadas");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    if (loading) {
        return (
            <div className="p-8 flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Cargando analytics…
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="tab-analytics-content">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h3 className="font-display text-2xl flex items-center gap-2">
                        <LineChart className="h-5 w-5 text-[color:var(--brand-dark-gold)]" />
                        Analytics de cartera
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                        Vintage curves · roll rates · WACF · concentraciones top-10.
                        Calculado sobre el corte de tape más reciente.
                        {readOnly && " Vista solo lectura para lender (roll rates internos omitidos)."}
                    </p>
                </div>
                {canRecalc && (
                    <Button onClick={recalc} disabled={busy}
                        data-testid="analytics-recalcular-btn">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" />
                              : <RefreshCw className="h-4 w-4 mr-1" />}
                        Recalcular
                    </Button>
                )}
            </div>

            {!analytics ? (
                <div className="rounded-md border border-dashed border-border p-10 text-center">
                    <TrendingUp className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                    <p className="text-sm text-muted-foreground">
                        Aún no hay analytics calculadas.
                    </p>
                    {canRecalc && (
                        <Button className="mt-4" onClick={recalc} disabled={busy}>
                            Calcular ahora
                        </Button>
                    )}
                </div>
            ) : (
                <>
                    {/* KPIs WACF */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="analytics-wacf-kpis">
                        <div className="kpi-card">
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                Tasa promedio ponderada
                            </div>
                            <div className="font-display text-2xl mt-1">
                                {pct((analytics.wacf?.tasa_promedio_ponderada || 0) * 100)}
                            </div>
                        </div>
                        <div className="kpi-card">
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                Duración prom. (meses)
                            </div>
                            <div className="font-display text-2xl mt-1">
                                {Number(analytics.wacf?.duracion_promedio_meses || 0).toFixed(1)}
                            </div>
                        </div>
                        <div className="kpi-card">
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                Top-10 concentración
                            </div>
                            <div className="font-display text-2xl mt-1">
                                {pct(analytics.wacf?.concentracion_top10_pct)}
                            </div>
                        </div>
                        <div className="kpi-card">
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                Créditos en cartera
                            </div>
                            <div className="font-display text-2xl mt-1">
                                {analytics.wacf?.num_creditos || 0}
                            </div>
                        </div>
                    </div>

                    {/* Vintage curves heatmap */}
                    <div className="rounded-md border border-border bg-card p-5">
                        <div className="flex items-center gap-2 mb-4">
                            <Grid className="h-4 w-4 text-[color:var(--brand-dark-gold)]" />
                            <h4 className="font-display text-lg">Vintage curves</h4>
                        </div>
                        <p className="text-[11px] text-muted-foreground mb-3">
                            Cohortes por mes de originación. Verde = 0%, gold = 1-5%, coral = 5%+.
                        </p>
                        <VintageHeatmap vintages={analytics.vintage_curves || {}} />
                    </div>

                    {/* Roll rates */}
                    {!readOnly && (
                        <div className="rounded-md border border-border bg-card p-5">
                            <div className="flex items-center gap-2 mb-4">
                                <Percent className="h-4 w-4 text-[color:var(--brand-dark-gold)]" />
                                <h4 className="font-display text-lg">Roll rates</h4>
                                <span className="text-[10px] uppercase tracking-widest text-muted-foreground ml-auto">
                                    Vista interna
                                </span>
                            </div>
                            <RollRatesMatrix rollRates={analytics.roll_rates || {}} />
                        </div>
                    )}

                    {/* Concentraciones */}
                    <div className="grid md:grid-cols-3 gap-4">
                        <ConcentrationList
                            title="Top-10 deudores"
                            icon={Users}
                            items={analytics.concentraciones?.top10_deudores}
                            testid="conc-deudores"
                        />
                        <ConcentrationList
                            title="Top-5 sectores"
                            icon={Briefcase}
                            items={analytics.concentraciones?.top5_sectores}
                            testid="conc-sectores"
                        />
                        <ConcentrationList
                            title="Top-5 geografía"
                            icon={MapPin}
                            items={analytics.concentraciones?.top5_geografia}
                            testid="conc-geografia"
                        />
                    </div>

                    <div className="flex items-center justify-between text-[10px] text-muted-foreground border-t border-border/60 pt-3">
                        <div>Calculado: {analytics.fecha_calculo?.slice(0, 16)?.replace("T", " ")}</div>
                        {!readOnly && analytics.hash_cadena && (
                            <div className="flex items-center gap-1">
                                <ShieldCheck className="h-3 w-3" />
                                Hash: <code>{analytics.hash_cadena?.slice(0, 12)}…</code>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
